import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMyFantasyFams, useQuarterlyLeaderboard } from "../../../lib/api/fantasy-fams";
import { useMySubscription } from "../../../lib/api/subscription";
import { BankrollTabs, LeaderboardRow, formatPctSigned, pctColorClass } from "./_shared";
import type { LeaderboardBankrollTier } from "../../../lib/types";

// /fantasy/quarterly — Fantasy Fams Quarterly leaderboard, one bankroll pool
// at a time. Raw return % is the hero metric; the SPY benchmark isn't tracked
// on the standalone entity yet, so `spyDelta` is null.
export function QuarterlyLeaderboardRoute() {
	const [bankroll, setBankroll] = useState<LeaderboardBankrollTier>("10k");
	const leaderboard = useQuarterlyLeaderboard(bankroll);

	// Free users can only open the $10K pool; $100K / $1M tabs are locked.
	const isPaid = useMySubscription().data?.status === "active";

	const myFams = useMyFantasyFams();
	const myFamSlugs = useMemo(
		() => new Set((myFams.data?.fantasyFams ?? []).map((f) => f.slug)),
		[myFams.data],
	);

	const data = leaderboard.data;
	const top = data?.entries[0];
	const topSpyDelta =
		top?.spyDelta !== undefined && top?.spyDelta !== null
			? parseFloat(top.spyDelta)
			: undefined;
	const topReturn = top ? parseFloat(top.returnPct) : undefined;

	return (
		<div className="mx-auto max-w-4xl space-y-6 px-4 py-8">
			<header className="space-y-1">
				<Link
					to="/fantasy"
					className="text-xs text-[var(--color-muted)] hover:text-[var(--color-fg)]"
				>
					← Fantasy
				</Link>
				<h1 className="text-2xl font-bold">Fantasy Fams: Quarterly</h1>
				<p className="text-sm text-[var(--color-muted)]">
					90-day composite lock. Each bankroll tier competes in its own pool.
				</p>
			</header>

			<BankrollTabs value={bankroll} onChange={setBankroll} isPaid={isPaid} />

			{leaderboard.isLoading && (
				<p className="text-sm text-[var(--color-muted)]">Loading…</p>
			)}
			{leaderboard.isError && (
				<p className="text-sm text-[var(--color-danger)]">
					Could not load leaderboard.{" "}
					{leaderboard.error instanceof Error ? leaderboard.error.message : ""}
				</p>
			)}

			{data && (
				<>
					{top ? (
						<section className="space-y-3 rounded-xl border border-[var(--color-accent)]/40 bg-gradient-to-br from-[var(--color-accent-soft)] to-transparent p-6">
							<div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
								<div>
									<p className="text-[11px] uppercase tracking-wider text-[var(--color-muted)]">
										Quarter leader
										{data.period ? ` · ${data.period}` : ""}
									</p>
									<p className="mt-1 text-2xl font-bold">
										{top.famName}
									</p>
									{top.famSlug && (
										<p className="font-mono text-xs text-[var(--color-muted)]">
											{top.famSlug}
										</p>
									)}
								</div>
								<div className="text-left sm:text-right">
									{topSpyDelta !== undefined ? (
										<>
											<p className="text-[11px] uppercase tracking-wider text-[var(--color-muted)]">
												vs SPY
											</p>
											<p
												className={`font-mono text-5xl font-bold tabular-nums sm:text-6xl ${pctColorClass(topSpyDelta)}`}
											>
												{formatPctSigned(topSpyDelta)}
											</p>
											<p className="mt-1 text-xs text-[var(--color-muted)]">
												raw{" "}
												<span
													className={`font-mono ${pctColorClass(topReturn)}`}
												>
													{formatPctSigned(topReturn)}
												</span>
											</p>
										</>
									) : (
										<>
											<p className="text-[11px] uppercase tracking-wider text-[var(--color-muted)]">
												return
											</p>
											<p
												className={`font-mono text-5xl font-bold tabular-nums sm:text-6xl ${pctColorClass(topReturn)}`}
											>
												{formatPctSigned(topReturn)}
											</p>
										</>
									)}
								</div>
							</div>
						</section>
					) : (
						<section className="rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-bg-elev)] p-6 text-center">
							<p className="text-sm text-[var(--color-muted)]">
								No quarterly entries yet
								{data.period ? ` for ${data.period}` : ""}. Paid Fams
								opted into Fantasy Fams Quarterly auto-enter at the next
								lock date.
							</p>
						</section>
					)}

					{data.entries.length > 1 && (
						<section className="space-y-2">
							<h2 className="text-sm font-semibold">Quarter standings</h2>
							{data.entries.slice(1).map((entry) => (
								<LeaderboardRow
									key={entry.famId}
									entry={entry}
									isYours={
										!!entry.famSlug && myFamSlugs.has(entry.famSlug)
									}
									emphasizeSpyDelta
								/>
							))}
						</section>
					)}
				</>
			)}

			<footer className="border-t border-[var(--color-border)] pt-4 text-center text-xs text-[var(--color-muted)]">
				Quarterly returns are 90+ day composite-weighted simulated paper trades,
				benchmarked to SPY.{" "}
				<a
					href="https://hedgefam.com/disclosures"
					className="underline hover:text-[var(--color-fg)]"
				>
					Disclosures
				</a>
				.
			</footer>
		</div>
	);
}
